import { Routes } from '@/CSIRouter.tsx';
import {
  Air,
  BatteryUnknown,
  Bolt,
  EvStation,
  Fireplace,
  OilBarrel,
  Propane,
  PropaneTank,
  Tsunami,
  Volcano,
  WbSunny,
} from '@mui/icons-material';
import {
  BuildingAlternative,
  BuildingParameters,
  OnSiteCostsTechnology,
  SubprojectBuildingDetails,
  SubprojectTechnology,
  TechnologyParameter,
} from '../stores/SubprojectStore';
import { EnergyKind, EnergyKinds, PassiveClassified, TabKinds } from './types';

export function iconForEnergySink(sink: EnergyKind) {
  switch (sink) {
    case 'Propane':
      return Propane;
    case 'Water':
    case 'Hydro':
      return Tsunami;
    case 'Diesel or Biodiesel':
    case 'Diesel':
      return OilBarrel;
    case 'Wind':
      return Air;
    case 'Electricity':
      return Bolt;
    case 'Electricity/ Gas':
      return EvStation;
    case 'LPG':
    case 'Natural Gas':
      return PropaneTank;
    case 'Solar':
      return WbSunny;
    case 'Geothermal':
      return Volcano;
    case 'Heat Capture':
      return Fireplace;
    case 'NA':
      return BatteryUnknown;
    default:
      return BatteryUnknown;
  }
}

// Some areas don't utilize the concept of an item limit so keeping for now
export function normalizeToRating(
  min: number,
  max: number,
  value: number,
  steps = 4,
) {
  // Handle edge case where all values are the same
  if (min === max) {
    // If all values are 0, treat as best rating (lowest emissions/cost)
    if (value === 0 && min === 0) {
      return 1;
    }
    // Otherwise, treat as middle rating
    return Math.ceil(steps / 2);
  }

  const normalized = (value - min) / (max - min);
  const clamp = 1 / steps;

  for (let i = 0; i < steps; i++) {
    const backStop = i * clamp;
    const nextStop = (i + 1) * clamp;
    if (normalized >= backStop && normalized < nextStop) {
      return i + 1;
    }
  }
  return steps;
}

export function normalizeToRatingLimit(
  min: number,
  max: number,
  value: number,
  limit: number,
  steps = 4,
) {
  // Handle edge case where all values are the same
  if (min === max) {
    // If all values are 0, treat as best rating (lowest emissions/cost)
    if (value === 0 && min === 0) {
      return 1;
    }
    // Otherwise, treat as middle rating
    return Math.ceil(steps / 2);
  }

  const normalized = (value * limit - min) / (max - min);
  const clamp = 1 / steps;

  for (let i = 0; i < steps; i++) {
    const backStop = i * clamp;
    const nextStop = (i + 1) * clamp;
    if (normalized >= backStop && normalized < nextStop) {
      return i + 1;
    }
  }

  return steps;
}

export const createEmptyEnergyDemand = (): Record<EnergyKind, number> => {
  return {
    'Diesel or Biodiesel': 0,
    LPG: 0,
    Water: 0,
    Propane: 0,
    Diesel: 0,
    Solar: 0,
    Wind: 0,
    Hydro: 0,
    Electricity: 0,
    'Electricity/ Gas': 0,
    Geothermal: 0,
    'Natural Gas': 0,
    'Heat Capture': 0,
    NA: 0,
  };
};

export const createEmptySubprojectTechnology = (
  tech: string,
): SubprojectTechnology => {
  return {
    technologyCategory: tech,
    params: [],
    costs: {
      capitalCost: 0,
      maintenanceCost: 0,
      energyCost: 0,
      replacementCosts: 0,
    },
    energyDemand: {
      'Diesel or Biodiesel': 0,
      LPG: 0,
      Water: 0,
      Propane: 0,
      Diesel: 0,
      Solar: 0,
      Wind: 0,
      Hydro: 0,
      Electricity: 0,
      'Electricity/ Gas': 0,
      Geothermal: 0,
      'Natural Gas': 0,
      'Heat Capture': 0,
      NA: 0,
    },
    demoCosts: { capitalCost: 0, maintenanceCost: 0, replacementCosts: 0 },
    demoEnergyDemand: {
      'Diesel or Biodiesel': 0,
      LPG: 0,
      Water: 0,
      Propane: 0,
      Diesel: 0,
      Solar: 0,
      Wind: 0,
      Hydro: 0,
      Electricity: 0,
      'Electricity/ Gas': 0,
      Geothermal: 0,
      'Natural Gas': 0,
      'Heat Capture': 0,
      NA: 0,
    },
  };
};

export const createEmptyBuildingAlternative = (): BuildingAlternative => {
  const emptyEnergyDemand: Record<string, number> = {};
  EnergyKinds.forEach((kind) => {
    emptyEnergyDemand[kind] = 0;
  });

  const emptyBuildingParams: BuildingParameters = {
    enabled: false,
    conditionedSpace: { value: 10_000, unit: 'sqft' },
    perviousArea: { value: 1_000, unit: 'sqft' },
    propertySize: { value: 100_000, unit: 'sqft' },
    buildingFootprint: { value: 200_000, unit: 'sqft' },
    numberOfFloors: 0,
    numberOfOccupants: 0,
  };

  return {
    buildingParams: emptyBuildingParams,
    technologyParams: { hvac: createEmptySubprojectTechnology('hvac') },
    onSiteEnergy: { params: [] },
    onSiteCosts: {
      capitalCost: 0,
      maintenanceCost: 0,
      majorMaintenanceCost: 0,
      regionalMultiplier: 0,
      technology: {} as Record<string, OnSiteCostsTechnology>,
    },
    externalEnergy: { params: [] },
    costs: { capitalCost: 0, maintenanceCost: 0, replacementCosts: 0 },
    energyDemand: {
      'Diesel or Biodiesel': 0,
      LPG: 0,
      Water: 0,
      Propane: 0,
      Diesel: 0,
      Solar: 0,
      Wind: 0,
      Hydro: 0,
      Electricity: 0,
      'Electricity/ Gas': 0,
      Geothermal: 0,
      'Natural Gas': 0,
      'Heat Capture': 0,
      NA: 0,
    },
    energyOnsiteSupply: emptyEnergyDemand,
    energyExternalDemand: emptyEnergyDemand,
    externalEnergyCosts: {},
    tonnesCo2: 0,
    demoFudgeCosts: { capitalCost: 0, maintenanceCost: 0, replacementCosts: 0 },
    demoFudgeEnergyDemand: emptyEnergyDemand,
  };
};

// Helper to ensure a building alternative has all required fields
export const ensureBuildingAlternativeComplete = (
  alt: any,
): BuildingAlternative => {
  const emptyAlt = createEmptyBuildingAlternative();

  if (!alt) {
    return emptyAlt;
  }

  // Merge with empty alternative to ensure all fields exist
  return {
    ...emptyAlt,
    ...alt,
    buildingParams: alt.buildingParams || emptyAlt.buildingParams,
    technologyParams: alt.technologyParams || emptyAlt.technologyParams,
    onSiteEnergy: alt.onSiteEnergy || emptyAlt.onSiteEnergy,
    onSiteCosts: alt.onSiteCosts || emptyAlt.onSiteCosts,
    externalEnergy: alt.externalEnergy || emptyAlt.externalEnergy,
    costs: alt.costs || emptyAlt.costs,
    energyDemand: alt.energyDemand || emptyAlt.energyDemand,
    energyOnsiteSupply: alt.energyOnsiteSupply || emptyAlt.energyOnsiteSupply,
    energyExternalDemand:
      alt.energyExternalDemand || emptyAlt.energyExternalDemand,
    externalEnergyCosts:
      alt.externalEnergyCosts || emptyAlt.externalEnergyCosts,
    tonnesCo2: alt.tonnesCo2 || emptyAlt.tonnesCo2,
    demoFudgeCosts: alt.demoFudgeCosts || emptyAlt.demoFudgeCosts,
    demoFudgeEnergyDemand:
      alt.demoFudgeEnergyDemand || emptyAlt.demoFudgeEnergyDemand,
  };
};

const getModulePath = (module: string) => {
  let modulePath = '';

  switch (module.toLowerCase()) {
    case 'lighting':
      modulePath = Routes.Project.Decarbonization.LightingSelection.path;
      break;
    case 'hvac':
      modulePath = Routes.Project.Decarbonization.HVACSelection.path;
      break;
    case 'waterpumps':
      modulePath = Routes.Project.Decarbonization.WaterPumpSelection.path;
      break;
    case 'waterheaters':
      modulePath = Routes.Project.Decarbonization.WaterHeaterSelection.path;
      break;
    case 'cooking':
      modulePath = Routes.Project.Decarbonization.CookingSelection.path;
      break;
    case 'plugload':
      modulePath = Routes.Project.Decarbonization.PlugLoadSelection.path;
      break;
    case 'monitoring':
      modulePath = Routes.Project.Decarbonization.MonitorSelection.path;
      break;
    case 'specialized':
      modulePath = Routes.Project.Decarbonization.SpecializedSelection.path;
      break;
    default:
      modulePath = ''; // default to project overview
      break;
  }

  return modulePath;
};

export const getPathToPrevDecarbModule = (
  currentSystemModule: string,
  enabledTechnologyCategories: string[],
): string => {
  const currentIdx = enabledTechnologyCategories.indexOf(currentSystemModule);
  const nextModule = enabledTechnologyCategories.at(currentIdx - 1);

  if (currentIdx === 0) {
    return '';
  }

  // current system module couldn't be found (likely an error in passed 'current' string)
  if (currentIdx === -1 || currentIdx === 0 || !nextModule) {
    return ''; // default to decarb overview
  }

  if (!nextModule) {
    return ''; // default to decarb overview
  }

  return getModulePath(nextModule);
};

export const getPathToNextDecarbModule = (
  currentSystemModule: string,
  enabledTechnologyCategories: string[],
): string => {
  const currentIdx = enabledTechnologyCategories.indexOf(currentSystemModule);
  const nextModule = enabledTechnologyCategories.at(currentIdx + 1);

  if (currentIdx === enabledTechnologyCategories.length - 1) {
    return '';
  }

  // current system module couldn't be found (likely an error in passed 'current' string)
  if (currentIdx === -1 || !nextModule) {
    return ''; // default to decarb overview
  }

  return getModulePath(nextModule);
};

export const getFirstSystemSelectionModulePath = (
  enabledTechnologyCategories: string[],
): string => {
  const module = enabledTechnologyCategories.at(0);

  if (!module) {
    return ''; // default to decarb overview
  }

  return getModulePath(module);
};

export const getLastSystemSelectionModulePath = (
  enabledTechnologyCategories: string[],
) => {
  const module = enabledTechnologyCategories.at(
    enabledTechnologyCategories.length - 1,
  );

  if (!module) {
    return ''; // default to decarb overview
  }

  return getModulePath(module);
};

/**
 * Ensures building params are synchronized across all three alternatives.
 * Building params (size, floors, occupants, etc.) should be identical for all alternatives.
 * This function copies the conventional alternative's building params to the other two.
 *
 * @param subproject - The subproject to sync
 * @returns A new subproject with synced building params
 */
export const syncBuildingParamsAcrossAlternatives = (
  subproject: SubprojectBuildingDetails,
): SubprojectBuildingDetails => {
  const updated = structuredClone(subproject);

  // Get building params from conventional alternative (source of truth)
  const conventionalParams = updated.conventionalAlternative?.buildingParams;

  if (!conventionalParams) {
    return updated;
  }

  // Sync to reduced carbon alternative
  if (updated.reducedCarbonAlternative) {
    updated.reducedCarbonAlternative.buildingParams =
      structuredClone(conventionalParams);
  }

  // Sync to zero carbon alternative
  if (updated.zeroCarbonAlternative) {
    updated.zeroCarbonAlternative.buildingParams =
      structuredClone(conventionalParams);
  }

  return updated;
};

/**
 * Maps technology category names (from enabledTechnologyCategories) to their storage keys (in technologyParams).
 * This handles the key mismatch between the hub display names and the actual storage keys.
 *
 * @param categoryName - The display name from enabledTechnologyCategories (e.g., "HVAC", "WaterPumps")
 * @returns The storage key used in technologyParams (e.g., "hvac", "waterpump")
 */
export const getTechnologyStorageKey = (categoryName: string): string => {
  const keyMap: Record<string, string> = {
    HVAC: 'hvac',
    Lighting: 'lighting',
    WaterPumps: 'waterpump',
    WaterHeaters: 'waterheaters', // Normalized to lowercase
    Cooking: 'cooking', // Normalized to lowercase
    Monitoring: 'monitoring', // Normalized to lowercase
    PlugLoad: 'plugload', // PlugLoad has its own separate key
    Specialized: 'specialized', // Normalized to lowercase
  };

  return keyMap[categoryName] || categoryName.toLowerCase();
};

/**
 * Maps storage keys (from technologyParams) back to their category names (in enabledTechnologyCategories).
 * This is the reverse of getTechnologyStorageKey.
 *
 * @param storageKey - The storage key used in technologyParams (e.g., "hvac", "waterpump")
 * @returns The category name from enabledTechnologyCategories (e.g., "HVAC", "WaterPumps")
 */
export const getCategoryFromStorageKey = (storageKey: string): string => {
  const reverseKeyMap: Record<string, string> = {
    hvac: 'HVAC',
    lighting: 'Lighting',
    waterpump: 'WaterPumps',
    waterheaters: 'WaterHeaters',
    cooking: 'Cooking',
    monitoring: 'Monitoring',
    plugload: 'PlugLoad', // PlugLoad has its own separate key
    specialized: 'Specialized',
    energy: 'Energy', // Energy sources (not in enabledTechnologyCategories, but used for navigation)
  };

  return reverseKeyMap[storageKey.toLowerCase()] || storageKey;
};

/**
 * Checks if a technology is passive (no energy consumption).
 * Passive technologies are building design changes that improve performance
 * without increasing energy demand (passive solar, passive ventilation, etc.).
 *
 * The single input is the `isPassive` flag served by `BaseTechnologySerializer`
 * on every `/api/data/common/technology/*` endpoint (`api_*.is_passive`, camelCase
 * on the wire). Do **not** re-derive this from `energySource` / `energySink`: that
 * free-text field disagrees with the catalog on several rows, which is exactly why
 * the explicit flag exists, and the backend (`calculations/orchestration.py`)
 * classifies off the same field.
 *
 * Passive technologies:
 * - Are flagged `isPassive = true` in the technology catalog
 * - Cost is calculated server-side as 15% of the building's user-entered design
 *   cost (see calculations/orchestration.py), not as a share of active tech costs
 * - Coverage is always 0% (they don't "cover" space in the traditional sense)
 * - Energy demand is 0
 *
 * A missing or undefined `isPassive` returns `false`. This is deliberate: a
 * technology whose flag failed to load must count toward coverage like any other
 * active technology rather than silently vanish from it.
 *
 * @param tech - A technology record from a `/api/data/common/technology/*` endpoint
 * @returns true if the technology is flagged passive, false otherwise
 */
export const isPassiveTechnology = (
  tech: PassiveClassified | undefined | null,
): boolean => tech?.isPassive === true;

/**
 * Fraction of a technology card's limit that its selected units cover, for the
 * pie in the lower-left corner of `SystemCard`.
 *
 * - An unselected card, or one with no count, covers nothing (`0`).
 * - A non-positive limit gives `0`, never `NaN` or `Infinity`.
 * - There is no cap: 7 units against a limit of 5 gives `1.4`.
 *
 * Category weights play no part: an efficiency-only HVAC card at its limit
 * shows a full pie though it adds 0% to the HVAC system pie, just as a
 * heating card at its limit shows a full pie and adds 30%. Passive cards show
 * no pie at all (`unitsFixed` on `SystemCard`), so they never reach here.
 */
export function getCardPieCoverage({
  count,
  limit,
  selected,
}: {
  count?: number;
  limit: number;
  selected: boolean;
}): number {
  if (!selected) return 0;
  if (!count || count <= 0) return 0;
  if (!limit || limit <= 0) return 0;
  return count / limit;
}

/**
 * Immutable update helper for technology params inside a SubprojectBuildingDetails.
 * Replaces structuredClone with targeted spreads for better performance.
 */
export function updateTechParams(
  prev: SubprojectBuildingDetails,
  tab:
    | 'conventionalAlternative'
    | 'reducedCarbonAlternative'
    | 'zeroCarbonAlternative',
  key: string,
  updater: (params: TechnologyParameter[]) => TechnologyParameter[],
): SubprojectBuildingDetails {
  const alt = prev[tab];
  const tech = alt.technologyParams[key];
  return {
    ...prev,
    [tab]: {
      ...alt,
      technologyParams: {
        ...alt.technologyParams,
        [key]: {
          ...tech,
          params: updater(tech.params),
        },
      },
    },
  };
}

/**
 * The capital cost the backend computed and stored for one technology category
 * on one alternative, or `undefined` when there is no figure to show (0 when
 * nothing is selected in the category).
 *
 * This is the ONLY source a category screen's tab badge and CAPITAL COST tile
 * may use. The stored total carries what local `capitalCostPerUnit x units`
 * arithmetic cannot: the regional multiplier, each catalog row's own
 * time-inflation ratio (its cost year against the latest ENR index), and
 * HVAC's passive 15%-of-design-cost charge. Because the inflation ratio differs
 * row by row, local arithmetic would be wrong by a different factor on each
 * alternative and corrupt the alternative-vs-conventional percentage, not just
 * the dollar figure. There is deliberately no fallback to arithmetic.
 *
 * `costsArePending` means the stored figure predates the selections on screen
 * (an unsaved edit), so there is no trustworthy figure yet.
 */
export function storedCategoryCapitalCost(
  alternative: BuildingAlternative | undefined,
  technologyKey: string,
  costsArePending: boolean,
): number | undefined {
  if (costsArePending || !alternative) {
    return undefined;
  }
  const entry = alternative.technologyParams?.[technologyKey];
  // Nothing selected in the category: its capital is genuinely 0. (The backend
  // drops categories with no selections from what it stores, so there is no
  // stored figure to read -- "--" there would look like a save that never
  // finished.) A stored null -- a failed compute -- still renders "--".
  if (!entry?.params?.length) {
    return 0;
  }
  const stored = entry.costs?.capitalCost;
  return typeof stored === 'number' && Number.isFinite(stored)
    ? stored
    : undefined;
}

/**
 * Whether two copies of a subproject hold the same selections for one
 * technology category on every alternative -- i.e. whether the stored cost
 * figures of `synced` (the last copy the server confirmed) still describe what
 * `current` shows.
 *
 * Compared by value, not identity: a refetch returning unchanged data keeps its
 * old object reference under react-query's structural sharing, so an identity
 * check could stay "pending" forever after an edit is undone.
 */
export function categorySelectionsMatch(
  current: SubprojectBuildingDetails | undefined,
  synced: SubprojectBuildingDetails | undefined,
  technologyKey: string,
): boolean {
  if (!current || !synced) {
    return current === synced;
  }
  const selections = (subproject: SubprojectBuildingDetails) =>
    TabKinds.map((tab) =>
      (subproject[tab]?.technologyParams?.[technologyKey]?.params ?? []).map(
        (param) => [String(param.techId), Number(param.units)],
      ),
    );
  return (
    JSON.stringify(selections(current)) === JSON.stringify(selections(synced))
  );
}

/**
 * Whether two copies of a subproject hold the same ENERGY selections on every
 * alternative -- on-site units (techId, units) and external rate plans (their
 * natural key) -- i.e. whether the energy screen's stored cost figures still
 * describe what `current` shows. By value, for the reason given on
 * categorySelectionsMatch (energy selections live outside technologyParams, so
 * that one does not apply).
 */
export function energySelectionsMatch(
  current: SubprojectBuildingDetails | undefined,
  synced: SubprojectBuildingDetails | undefined,
): boolean {
  if (!current || !synced) {
    return current === synced;
  }
  const selections = (subproject: SubprojectBuildingDetails) =>
    TabKinds.map((tab) => [
      (subproject[tab]?.onSiteEnergy?.params ?? []).map((param) => [
        String(param.techId),
        Number(param.units),
      ]),
      (subproject[tab]?.externalEnergy?.params ?? []).map(
        (param) =>
          `${param.utilityIdentifier}:${param.ratePlanType}:${param.state}:${param.sector}`,
      ),
    ]);
  return (
    JSON.stringify(selections(current)) === JSON.stringify(selections(synced))
  );
}

/**
 * The energy screen's cost figures for one alternative, as the backend
 * computed and stored them; `undefined` for a figure there is none to show.
 *
 *   capitalCost: on-site ("internal energy") capital -- `onSiteCosts.capitalCost`,
 *     which carries each on-site row's inflation ratio, the regional
 *     multiplier and nothing else. External energy has no capital.
 *   annualCost: on-site O&M plus the annual energy cost of every building
 *     system -- `onSiteCosts.maintenanceCost` + Σ technologyParams[*].costs
 *     .energyCost -- the energy-and-on-site part of the Decarb Hub's annual
 *     pie, net of the on-site generation credit and charging each energy
 *     source once.
 *
 * Both are `undefined` while `costsArePending` (an unsaved energy edit) and
 * when the alternative has no stored on-site figures (never computed, or its
 * compute failed and the costs were cleared). There is deliberately no
 * fallback to client-side arithmetic -- see storedCategoryCapitalCost.
 */
export function storedEnergyCosts(
  alternative: BuildingAlternative | undefined,
  costsArePending: boolean,
): { capitalCost?: number; annualCost?: number } {
  if (costsArePending || !alternative) {
    return {};
  }
  const finite = (value: unknown): value is number =>
    typeof value === 'number' && Number.isFinite(value);
  const onSite = alternative.onSiteCosts;
  const capitalCost = finite(onSite?.capitalCost)
    ? onSite.capitalCost
    : undefined;
  if (!finite(onSite?.maintenanceCost)) {
    return { capitalCost };
  }
  const energyCost = Object.values(alternative.technologyParams ?? {}).reduce(
    (sum, entry) => {
      const cost = entry?.costs?.energyCost;
      return finite(cost) ? sum + cost : sum;
    },
    0,
  );
  return { capitalCost, annualCost: onSite.maintenanceCost + energyCost };
}
